import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Icon,
  Text,
  Flex,
  Button,
  useDisclosure,
  Box,
} from '@wegroup/design-system';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  CalculationSpec,
  isOfferSuccessfull,
  LouiseOffer,
} from '../../../../../../../../types/Quotes';
import {
  getProductKey,
  OfferState,
} from '../../../../../services/offersService';
import { QuoteSpecifications } from '../../../../../store/redux/slices/offersSlice';
import OfferAction from './OfferAction';
import OfferParametersGrid from './parameters/OfferParametersGrid';

interface Props {
  isDisabled?: boolean;
  offer: LouiseOffer;
  quoteSpecs: QuoteSpecifications;
  onQuoteSpecsChange: (quoteSpecs: QuoteSpecifications) => void;
}

export const EXCLUDED_CALCULATION_SPECS = ['PAYMENT_INTERVAL'];

const OfferParametersChangeAction: React.FC<Props> = ({
  isDisabled,
  offer,
  quoteSpecs,
  onQuoteSpecsChange,
}) => {
  const { t } = useTranslation();

  const { isOpen, onClose, onOpen } = useDisclosure();

  const [calculationSpecs, setCalculationSpecs] = useState<CalculationSpec[]>();
  useEffect(() => {
    if (offer.state === OfferState.FETCHED && isOfferSuccessfull(offer)) {
      // Sets the initial values if the quote was ever retrieved
      const filteredSpecs = offer.details?.calculation_specs?.filter(
        ({ calc_type }) => !EXCLUDED_CALCULATION_SPECS.includes(calc_type),
      );
      setCalculationSpecs(filteredSpecs);
    }
  }, [offer]);

  const methods = useForm({
    values: Object.assign(
      {},
      ...(calculationSpecs?.map(({ calc_type, specs_field, value }) => ({
        [calc_type]: quoteSpecs[getProductKey(offer)]?.[specs_field] || value,
      })) || []),
    ),
  });

  const handleSubmit = methods.handleSubmit((data) => {
    // Create a mapping for the calc_type -> specs_field
    const mapping = Object.assign(
      {},
      ...calculationSpecs!.map(({ calc_type, specs_field }) => ({
        [calc_type]: specs_field,
      })),
    );

    /**
     * Use the mapping to create a new object with the specs_field as key
     * If the key is not present in the mapping, it will be ignored
     */
    const mappedData = Object.assign(
      {},
      ...Object.entries(data)
        .map(([key, value]) => {
          if (mapping[key] != null)
            return {
              [mapping[key]]: value,
            };
          return null;
        })
        .filter((val) => val != null),
    );

    onQuoteSpecsChange(mappedData);

    onClose();
  });

  // Because the calculationspecs are shown in the header for boat, we hardcode it here
  if (!calculationSpecs?.length || offer.info.insurance_type === 'boat')
    return <Box bg="white" />;

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
      <PopoverTrigger>
        <OfferAction
          icon={<Icon name="GeneralActionsEditDocument" />}
          isDisabled={isDisabled || !calculationSpecs?.length}
        >
          {t('SALES-CONVERSATION_OFFERS_ACTIONS_CHANGE-PARAMETERS')}
        </OfferAction>
      </PopoverTrigger>
      {!isDisabled && (
        <PopoverContent w="100%">
          <PopoverArrow />
          <PopoverCloseButton size="md" />
          <PopoverHeader>
            <Flex alignItems="center">
              <Text textStyle="normalBodyBold" as="h1" mr="2">
                {t('Parameters')}
              </Text>
            </Flex>
          </PopoverHeader>
          <PopoverBody>
            <FormProvider {...methods}>
              {calculationSpecs && (
                <OfferParametersGrid calculationSpecs={calculationSpecs} />
              )}
              <Flex mt="2" justify="flex-end">
                <Button
                  onClick={handleSubmit}
                  isLoading={methods.formState.isSubmitting}
                >
                  {t('Apply')}
                </Button>
              </Flex>
            </FormProvider>
          </PopoverBody>
        </PopoverContent>
      )}
    </Popover>
  );
};

export default OfferParametersChangeAction;
